import React, { useState } from 'react';
import { Eye, MoreHorizontal, Pencil, RadioTower, Trash2, TriangleAlert } from 'lucide-react';
import { Button, Card, EmptyState, IconButton, Menu, Pill, Skeleton, Spinner } from '../../../components/ui';
import type { ScanResult, SubjectRef, UpdateWatchInput, WatchView } from '../../../types/monitoring';
import { FREQUENCY_LABEL, timeAgo, timeUntil } from '../format';
import { MetaLine, SubjectLink } from './ui';
import { SubjectPicker } from './SubjectPicker';
import { WatchEditorDialog } from './WatchEditorDialog';

const frequencyTone = (frequency: WatchView['frequency']) => (frequency === 'paused' ? 'neutral' : 'outline');

/** What one scan actually did, in one line */
export const scanSummary = (result: ScanResult): string =>
  result.error
    ? result.error
    : `Found ${result.found} · ${result.stored} new · ${result.duplicates} already seen${
        result.alertsRaised ? ` · ${result.alertsRaised} alert${result.alertsRaised === 1 ? '' : 's'}` : ''
      }`;

export interface WatchlistProps {
  watches: WatchView[];
  loading: boolean;
  busyId: string | null;
  canScan: boolean;
  onAdd: (subject: SubjectRef) => void;
  onUpdate: (id: string, input: UpdateWatchInput) => void;
  onRemove: (id: string) => void;
  onScan: (id: string) => Promise<ScanResult | null>;
  /** A scan is running somewhere, so another cannot start */
  scanRunning?: boolean;
  notice?: React.ReactNode;
}

export const Watchlist: React.FC<WatchlistProps> = ({
  watches,
  loading,
  busyId,
  canScan,
  onAdd,
  onUpdate,
  onRemove,
  onScan,
  scanRunning = false,
  notice,
}) => {
  const [editing, setEditing] = useState<WatchView | null>(null);
  const [scans, setScans] = useState<Record<string, ScanResult>>({});
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);

  const runScan = async (id: string) => {
    const result = await onScan(id);
    if (result) setScans((current) => ({ ...current, [id]: result }));
  };

  return (
    <>
      <div className="mb-3">
        <SubjectPicker
          className="w-full sm:w-80"
          placeholder="Add a person or organization"
          ariaLabel="Add a person or organization to the watchlist"
          exclude={watches.map((watch) => watch.subject.id)}
          onPick={onAdd}
        />
      </div>

      {notice && <div className="mb-3">{notice}</div>}

      {loading ? (
        <Card divided aria-busy="true" aria-label="Loading watchlist">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="flex items-center gap-3 px-4 py-3">
              <Skeleton className="h-6 w-6 rounded-full" />
              <Skeleton className="h-4 w-40" />
            </div>
          ))}
        </Card>
      ) : watches.length === 0 ? (
        <Card>
          <EmptyState icon={Eye} title="Nothing is being watched yet — add a person or organization above" />
        </Card>
      ) : (
        <Card divided>
          {watches.map((watch) => {
            const busy = busyId === watch.id || watch.scanning;
            const scan = scans[watch.id];
            const error = scan?.error || watch.lastError;
            return (
              <div key={watch.id} className="px-4 py-3">
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <SubjectLink subject={watch.subject} />
                      <Pill tone={frequencyTone(watch.frequency)}>{FREQUENCY_LABEL[watch.frequency]}</Pill>
                      {busy && <Spinner className="h-3 w-3 text-neutral-400" />}
                      {watch.scanning && <span className="text-[12px] text-neutral-500">Scanning now…</span>}
                    </div>
                    <MetaLine
                      className="mt-1"
                      parts={[
                        watch.lastScanAt ? `Scanned ${timeAgo(watch.lastScanAt)}` : 'Never scanned',
                        watch.frequency !== 'paused' && watch.nextScanAt ? `next ${timeUntil(watch.nextScanAt)}` : '',
                        `${watch.recentSignals} in 30 days`,
                        `${watch.signalCount} total`,
                        watch.keywords.length ? `${watch.keywords.length} keyword${watch.keywords.length === 1 ? '' : 's'}` : '',
                      ]}
                    />
                    {scan && !scan.error && <p className="mt-1 text-[12px] text-neutral-500">{scanSummary(scan)}</p>}
                    {error && (
                      <p className="mt-1 flex items-start gap-1.5 text-[12px] text-primary-700">
                        <TriangleAlert className="mt-px h-3.5 w-3.5 flex-shrink-0" strokeWidth={1.75} />
                        <span className="min-w-0">Last scan failed — {error}</span>
                      </p>
                    )}
                  </div>

                  <div className="flex flex-shrink-0 items-center gap-1">
                    {canScan && (
                      <Button
                        size="xs"
                        variant="ghost"
                        disabled={busy || scanRunning}
                        title={scanRunning && !watch.scanning ? 'A scan is already running' : undefined}
                        onClick={() => runScan(watch.id)}
                      >
                        <RadioTower className="h-3.5 w-3.5" strokeWidth={1.75} />
                        Scan
                      </Button>
                    )}
                    <Menu
                      trigger={(props) => (
                        <IconButton label={`Actions for ${watch.subject.label}`} size="sm" {...props}>
                          <MoreHorizontal className="h-4 w-4" strokeWidth={1.75} />
                        </IconButton>
                      )}
                      items={[
                        { label: 'Edit', icon: Pencil, onSelect: () => setEditing(watch) },
                        {
                          label: confirmRemove === watch.id ? 'Remove — confirm' : 'Remove',
                          icon: Trash2,
                          danger: true,
                          onSelect: () => {
                            if (confirmRemove === watch.id) {
                              setConfirmRemove(null);
                              onRemove(watch.id);
                            } else {
                              setConfirmRemove(watch.id);
                            }
                          },
                        },
                      ]}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </Card>
      )}

      <WatchEditorDialog
        watch={editing}
        saving={!!editing && busyId === editing.id}
        onSave={(id, input) => {
          onUpdate(id, input);
          setEditing(null);
        }}
        onClose={() => setEditing(null)}
      />
    </>
  );
};
